'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { z } from 'zod';

import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { SlideOver } from './SlideOver';
import { api, ApiError } from '@/lib/api';

interface StaffRow {
  id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'front_desk';
  active: boolean;
  createdAt: string;
}

interface InvitationRow {
  id: string;
  name: string;
  email: string;
  role: string;
  expiresAt?: string | null;
}

const inviteSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter a first name').max(80),
  lastName: z.string().trim().min(1, 'Enter a last name').max(80),
  email: z.string().trim().email('Enter a valid email address').max(191),
  role: z.enum(['super_admin', 'front_desk']),
});

const inputCls =
  'w-full rounded-2xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand focus:bg-card';

export function StaffModule({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [invites, setInvites] = useState<InvitationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [revokePending, setRevokePending] = useState(false);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([api<StaffRow[]>('/api/users'), api<InvitationRow[]>('/api/users/invitations')])
      .then(([list, pending]) => {
        if (cancelled) return;
        setRows(list);
        setInvites(pending);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load the staff list');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage]);

  if (!canManage) {
    return (
      <p className="rounded-3xl border border-line bg-card p-6 text-sm text-muted">
        Front desk can take bookings and handovers. Staff accounts are changed by the agency owner.
      </p>
    );
  }

  async function patch(id: string, body: { role?: StaffRow['role']; active?: boolean }) {
    setError(null);
    const previous = rows;
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...body } : row)));
    try {
      const updated = await api<StaffRow>(`/api/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setRows((current) => current.map((row) => (row.id === id ? updated : row)));
    } catch (err: unknown) {
      setRows(previous);
      setError(err instanceof ApiError ? err.message : 'Could not update this person');
    }
  }

  async function revoke() {
    if (!revokeId) return;
    setRevokePending(true);
    setError(null);
    try {
      await api(`/api/users/invitations/${revokeId}`, { method: 'DELETE' });
      setInvites((current) => current.filter((invite) => invite.id !== revokeId));
      setRevokeId(null);
      setNotice('Invitation revoked.');
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke this invitation');
    } finally {
      setRevokePending(false);
    }
  }

  const revokeTarget = invites.find((invite) => invite.id === revokeId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-3xl border border-line bg-card p-4">
        <div>
          <p className="flex items-center gap-2 text-sm font-bold text-ink">
            <UserPlus size={16} /> Invite a colleague
          </p>
          <p className="mt-1 text-xs text-muted">They choose their own password from the link. The link expires in 7 days.</p>
        </div>
        <button type="button" onClick={() => setInviting(true)} className="rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell">
          Invite
        </button>
      </div>

      {error && <p className="rounded-2xl bg-danger/10 px-3 py-2 text-xs font-semibold text-danger">{error}</p>}
      {notice && <p className="rounded-2xl bg-success/10 px-3 py-2 text-xs font-semibold text-success">{notice}</p>}

      <div className="overflow-hidden rounded-3xl border border-line bg-card">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3 text-sm font-bold text-ink">
          <ShieldCheck size={16} /> Team
        </div>
        {loading ? (
          <p className="px-4 py-6 text-xs text-muted">Loading staff…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted">No staff accounts for this agency yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <li key={row.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-ink">{row.name}</p>
                  <p className="truncate text-xs text-muted">{row.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={row.role}
                    onChange={(event) => void patch(row.id, { role: event.target.value as StaffRow['role'] })}
                    className={inputCls}
                  >
                    <option value="front_desk">Front desk</option>
                    <option value="super_admin">Owner</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => void patch(row.id, { active: !row.active })}
                    className={`rounded-2xl px-3 py-2 text-xs font-bold ${row.active ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}
                  >
                    {row.active ? 'Active' : 'Suspended'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="overflow-hidden rounded-3xl border border-line bg-card">
        <div className="border-b border-line px-4 py-3 text-sm font-bold text-ink">Pending invitations</div>
        {invites.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted">No invitations are waiting.</p>
        ) : (
          <ul className="divide-y divide-line">
            {invites.map((invite) => (
              <li key={invite.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-ink">{invite.name}</p>
                  <p className="truncate text-xs text-muted">{invite.email} · {invite.role === 'super_admin' ? 'Owner' : 'Front desk'}</p>
                </div>
                <button type="button" onClick={() => setRevokeId(invite.id)} className="rounded-2xl px-3 py-2 text-xs font-bold text-danger">
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {inviting && (
        <InviteSlideOver
          onClose={() => setInviting(false)}
          onCreated={(invite) => {
            setInvites((current) => [invite, ...current.filter((row) => row.email !== invite.email)]);
            setInviting(false);
            setNotice(`Invitation sent to ${invite.email}.`);
          }}
        />
      )}
      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke invitation"
        description={revokeTarget ? `Revoke the invitation for ${revokeTarget.email}. The link will stop working.` : 'Revoke this invitation.'}
        confirmLabel="Revoke"
        pending={revokePending}
        onCancel={() => { if (!revokePending) setRevokeId(null); }}
        onConfirm={() => void revoke()}
      />
    </div>
  );
}

function InviteSlideOver({ onClose, onCreated }: { onClose: () => void; onCreated: (invite: InvitationRow) => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRow['role']>('front_desk');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function invite(event: FormEvent) {
    event.preventDefault();
    const parsed = inviteSchema.safeParse({ firstName, lastName, email, role });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    setError(null);
    setSaving(true);
    try {
      const created = await api<InvitationRow>('/api/users', {
        method: 'POST',
        body: JSON.stringify({
          firstName: parsed.data.firstName,
          lastName: parsed.data.lastName,
          name: `${parsed.data.firstName} ${parsed.data.lastName}`,
          email: parsed.data.email,
          role: parsed.data.role,
        }),
      });
      onCreated(created);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not send this invitation');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SlideOver
      open
      title="Invite a colleague"
      onClose={onClose}
      footer={
        <button type="submit" form="staff-invite" disabled={saving} className="rounded-2xl bg-ink px-4 py-2 text-xs font-semibold text-shell disabled:opacity-50">
          {saving ? 'Sending…' : 'Send invitation'}
        </button>
      }
    >
      <form id="staff-invite" onSubmit={(event) => void invite(event)} className="space-y-3">
        <label className="block text-xs font-medium text-muted">
          First name *
          <input required value={firstName} onChange={(event) => setFirstName(event.target.value)} className={inputCls} />
          {fieldErrors.firstName && <span className="mt-1 block text-danger">{fieldErrors.firstName}</span>}
        </label>
        <label className="block text-xs font-medium text-muted">
          Last name *
          <input required value={lastName} onChange={(event) => setLastName(event.target.value)} className={inputCls} />
          {fieldErrors.lastName && <span className="mt-1 block text-danger">{fieldErrors.lastName}</span>}
        </label>
        <label className="block text-xs font-medium text-muted">
          Email *
          <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} className={inputCls} />
          {fieldErrors.email && <span className="mt-1 block text-danger">{fieldErrors.email}</span>}
        </label>
        <label className="block text-xs font-medium text-muted">
          Role *
          <select value={role} onChange={(event) => setRole(event.target.value as StaffRow['role'])} className={inputCls}>
            <option value="front_desk">Front desk</option>
            <option value="super_admin">Owner</option>
          </select>
        </label>
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}
